import React, { memo, useEffect, useRef, useState } from 'react';
import { ChevronDown, Cross, Plus } from '@saleshandy/icons';
import { Button, ImageIcon } from '@saleshandy/design-system';
import { useSelector } from 'react-redux';
import { useHistory, useLocation } from 'react-router-dom';
import { Preview } from '../../../../../shared/svg/preview';
import AiColSaveAsTemplateModal from './ai-col-save-as-template-modal';
import { accessibleOnClick } from '../../../../../shared/utils/accessible-on-click';
import { TemplateFooterProps } from '../type';
import { AiColEnrichmentModalSteps, AiColumnsActionType } from '../enums';
import { Images } from '../../../../../shared/app-constants';
import {
  OverlayPopover,
  OverlayTooltip,
  Placement,
} from '../../../../../shared/design-system/components/overlay';
import { RootState } from '../../../../../store/root-reducer';
import AiColPromptPreviewModal from './ai-col-prompt-preview-modal';
import OverrideGeneratedExistingColumn from './override-generated-existing-column';
import { formatNumber } from '../../../../sequence/components/sequence-single-content/utils/helper';

const TemplateFooter: React.FC<TemplateFooterProps> = memo(
  ({
    saveAndDontRun,
    disableFooter = false,
    footerDisabled = false,
    enrichPromptData,
    templateId,
    templateData,
    setPreviewModalOpen,
    currentStep,
    selectedModalAiCreditUsagePerRow,
    bulkProspectSelected,
    isSaveDisabledForColumnName,
    resetBulkProspectSelected,
    isNewColumnName,
  }) => {
    const history = useHistory();
    const location = useLocation();
    const [openPopover, setOpenPopover] = useState(null);
    const [savePopoverOpen, setSavePopoverOpen] = useState(false);
    const [overrideModalOpen, setOverrideModalOpen] = useState(false);
    const [
      pendingAction,
      setPendingAction,
    ] = useState<AiColumnsActionType | null>(null);
    const [adjustedSelectionCount, setAdjustedSelectionCount] = useState(0);
    const [isLoadingState, setIsLoadingState] = useState(false);
    const popoverRef = useRef<HTMLDivElement | null>(null);
    const cellRef = useRef<HTMLDivElement | null>(null);
    const btnRef = useRef<HTMLButtonElement>(null);

    const handleCreditsPopoverToggle = (e, key) => {
      e.stopPropagation();
      setOpenPopover(openPopover === key ? null : key);
    };

    const handleOverrideModalClose = () => {
      setOverrideModalOpen(false);
      setPendingAction(null);
    };

    const handleOverrideModalProceed = () => {
      if (pendingAction) {
        setOverrideModalOpen(false);
        saveAndDontRun(pendingAction);
        setPendingAction(null);
      }
    };

    const handleRedirectToListView = () => {
      if (location.pathname === '/crm/kanban') {
        history.push('/crm/list');
      }
    };

    const handleSaveAction = (actionType: AiColumnsActionType) => {
      if (
        currentStep === AiColEnrichmentModalSteps.EXISTING_COLUMN &&
        !isNewColumnName &&
        (actionType === AiColumnsActionType.SaveAndRunForAllRows ||
          actionType === AiColumnsActionType.SaveAndRunForSelectedRows ||
          actionType === AiColumnsActionType.SaveAndRunForFirst10Rows)
      ) {
        setPendingAction(actionType);
        setOverrideModalOpen(true);
        setSavePopoverOpen(false);
      } else {
        setSavePopoverOpen(false);
        saveAndDontRun(actionType);
      }
    };
    const selectedProspectRowIds = useSelector(
      (state: RootState) => state?.prospect?.selectedProspectRowId,
    );

    const totalProspectList = useSelector(
      (state: RootState) => state?.prospect?.prospectCount,
    );
    const prospectList = useSelector(
      (state: RootState) => state?.prospect?.prospects,
    );

    const deselectedProspectRowId = useSelector(
      (state: RootState) => state?.prospect?.deSelectedProspectRowId,
    );

    useEffect(() => {
      const count = bulkProspectSelected
        ? totalProspectList - (deselectedProspectRowId?.length || 0)
        : selectedProspectRowIds?.length || 0;
      setAdjustedSelectionCount(count);
    }, [bulkProspectSelected, deselectedProspectRowId, selectedProspectRowIds]);

    useEffect(() => {
      if (!savePopoverOpen) return;

      function handleClickOutside(e: MouseEvent) {
        const target = e.target as HTMLElement;

        // ✅ Ignore clicks inside main dropdown OR any nested popover
        if (
          target.closest('.save-dropdown') ||
          target.closest('.credits-popover')
        ) {
          return;
        }

        if (
          cellRef.current &&
          !cellRef.current.contains(target) &&
          !btnRef.current?.contains(target)
        ) {
          setSavePopoverOpen(false);
        }
      }

      document.addEventListener('mousedown', handleClickOutside);
      // eslint-disable-next-line consistent-return
      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }, [savePopoverOpen]);

    useEffect(() => {
      if (!openPopover) return;

      function handleClickOutside(e: MouseEvent) {
        // Ignore clicks inside Select/Transfer overlay dropdowns
        if ((e.target as HTMLElement)?.closest('.credits-popover')) {
          return;
        }
        if (
          cellRef.current &&
          !cellRef.current.contains(e.target as Node) &&
          !popoverRef.current?.contains(e.target as Node)
        ) {
          setOpenPopover(false);
          setSavePopoverOpen(true);
        }
      }

      document.addEventListener('mousedown', handleClickOutside);
      // eslint-disable-next-line consistent-return
      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }, [openPopover]);

    const options = [
      {
        listKey: '10-rows',
        listLabel: (
          <>
            <div className="save-credits">
              <span className="save-option">
                {isNewColumnName
                  ? 'Save & Run For 10 Rows'
                  : 'Override & Update 10 Rows'}
              </span>
            </div>
            <OverlayPopover
              className="credits-popover"
              content={
                <div className="credits-popover-content">
                  <div className="credits-popover-text">
                    <div className="title">
                      Credits shown are estimated. It may vary based upon token
                      consumption.
                    </div>
                    <a
                      href="https://docs.saleshandy.com/en/articles/12412342-a-complete-guide-to-saleshandy-ai-credits"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="learn-more"
                      onClick={(e) => {
                        e.stopPropagation();
                      }}
                    >
                      Learn More
                    </a>
                  </div>
                  <div className="cross-icon-container">
                    <Cross
                      width={15}
                      height={15}
                      className="cross-icon"
                      {...accessibleOnClick((e) =>
                        handleCreditsPopoverToggle(e, '10-rows'),
                      )}
                    />
                  </div>
                </div>
              }
              placement={Placement.Top}
              show={openPopover === '10-rows'}
              onToggle={(isOpen) => setOpenPopover(isOpen ? '10-rows' : null)}
              // rootClose={true}
            >
              {selectedModalAiCreditUsagePerRow * 10 > 999 ? (
                <OverlayTooltip text={selectedModalAiCreditUsagePerRow * 10}>
                  <div
                    className="credits"
                    {...accessibleOnClick((e) => e.stopPropagation())}
                  >
                    <ImageIcon src={Images?.AiCredits} alt="credits" />
                    <span
                      className="credits-amount"
                      {...accessibleOnClick((e) => {
                        handleCreditsPopoverToggle(e, '10-rows');
                      })}
                    >
                      ~
                      {`${formatNumber(
                        Number(
                          (selectedModalAiCreditUsagePerRow * 10)?.toFixed(2),
                        ),
                      )}`}
                    </span>
                  </div>
                </OverlayTooltip>
              ) : (
                <div
                  className="credits"
                  {...accessibleOnClick((e) => e.stopPropagation())}
                >
                  <ImageIcon src={Images?.AiCredits} alt="credits" />
                  <span
                    className="credits-amount"
                    {...accessibleOnClick((e) => {
                      // setSavePopoverOpen(false);
                      handleCreditsPopoverToggle(e, '10-rows');
                    })}
                  >
                    {`${formatNumber(
                      Number(
                        (selectedModalAiCreditUsagePerRow * 10)?.toFixed(2),
                      ),
                    )}`}
                  </span>
                </div>
              )}
            </OverlayPopover>
          </>
        ),
        ...accessibleOnClick(() => {
          handleSaveAction(AiColumnsActionType.SaveAndRunForFirst10Rows);
        }),
      },
      {
        listKey: 'all-prospects',
        listLabel: (
          <>
            <div className="save-credits">
              <span className="save-option">
                {isNewColumnName
                  ? 'Save & Run For All Rows'
                  : 'Override & Update All Rows'}
              </span>
              {totalProspectList > 999 ? (
                <OverlayTooltip text={totalProspectList}>
                  <span className="total-prospects">
                    {formatNumber(totalProspectList)}
                  </span>
                </OverlayTooltip>
              ) : (
                <span className="total-prospects">
                  {formatNumber(totalProspectList)}
                </span>
              )}
            </div>
            <OverlayPopover
              className="credits-popover"
              content={
                <div className="credits-popover-content">
                  <div className="credits-popover-text">
                    <div className="title">
                      Credits shown are estimated. It may vary based upon token
                      consumption.
                    </div>
                    <a
                      href="https://docs.saleshandy.com/en/articles/12412342-a-complete-guide-to-saleshandy-ai-credits"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="learn-more"
                      onClick={(e) => {
                        e.stopPropagation();
                      }}
                    >
                      Learn More
                    </a>
                  </div>
                  <div className="cross-icon-container">
                    <Cross
                      width={15}
                      height={15}
                      className="cursor-pointer"
                      {...accessibleOnClick((e) =>
                        handleCreditsPopoverToggle(e, 'all-prospects'),
                      )}
                    />
                  </div>
                </div>
              }
              placement={Placement.Top}
              show={openPopover === 'all-prospects'}
              onToggle={(isOpen) =>
                setOpenPopover(isOpen ? 'all-prospects' : null)
              }
              // rootClose={true}
            >
              {selectedModalAiCreditUsagePerRow * totalProspectList > 999 ? (
                <OverlayTooltip
                  text={selectedModalAiCreditUsagePerRow * totalProspectList}
                >
                  <div
                    className="credits"
                    {...accessibleOnClick((e) => e.stopPropagation())}
                  >
                    <ImageIcon src={Images?.AiCredits} alt="credits" />
                    <span
                      className="credits-amount"
                      {...accessibleOnClick((e) => {
                        handleCreditsPopoverToggle(e, 'all-prospects');
                      })}
                    >
                      ~
                      {`${formatNumber(
                        Number(
                          (
                            selectedModalAiCreditUsagePerRow * totalProspectList
                          ).toFixed(2),
                        ),
                      )}`}
                    </span>
                  </div>
                </OverlayTooltip>
              ) : (
                <div
                  className="credits"
                  {...accessibleOnClick((e) => e.stopPropagation())}
                >
                  <ImageIcon src={Images?.AiCredits} alt="credits" />
                  <span
                    className="credits-amount"
                    {...accessibleOnClick((e) => {
                      // setSavePopoverOpen(false);
                      handleCreditsPopoverToggle(e, 'all-prospects');
                    })}
                  >
                    ~
                    {`${formatNumber(
                      Number(
                        (
                          selectedModalAiCreditUsagePerRow * totalProspectList
                        ).toFixed(2),
                      ),
                    )}`}
                  </span>
                </div>
              )}
            </OverlayPopover>
          </>
        ),
        ...accessibleOnClick(() => {
          handleSaveAction(AiColumnsActionType.SaveAndRunForAllRows);
        }),
      },
      // Save and run for selected rows
      ...(adjustedSelectionCount > 0
        ? [
            {
              listKey: 'selected-row',
              listLabel: (
                <>
                  <div className="save-credits">
                    <span className="save-option">
                      {isNewColumnName
                        ? 'Save & Run For Selected Rows'
                        : 'Override & Update Selected Rows'}
                    </span>
                    {adjustedSelectionCount > 999 ? (
                      <OverlayTooltip text={adjustedSelectionCount}>
                        <span className="total-prospects">
                          {formatNumber(adjustedSelectionCount)}
                        </span>
                      </OverlayTooltip>
                    ) : (
                      <span className="total-prospects">
                        {formatNumber(adjustedSelectionCount)}
                      </span>
                    )}
                  </div>
                  <OverlayPopover
                    className="credits-popover"
                    content={
                      <div className="credits-popover-content">
                        <div className="credits-popover-text">
                          <div className="title">
                            Credits shown are estimated. It may vary based upon
                            token consumption.
                          </div>
                          <a
                            href="https://docs.saleshandy.com/en/articles/12412342-a-complete-guide-to-saleshandy-ai-credits"
                            target="_blank"
                            rel="noopener noreferrer"
                            className="learn-more"
                            onClick={(e) => {
                              e.stopPropagation();
                            }}
                          >
                            Learn More
                          </a>
                        </div>
                        <div className="cross-icon-container">
                          <Cross
                            width={15}
                            height={15}
                            className="cursor-pointer"
                            {...accessibleOnClick((e) =>
                              handleCreditsPopoverToggle(e, 'selected-row'),
                            )}
                          />
                        </div>
                      </div>
                    }
                    placement={Placement.Top}
                    show={openPopover === 'selected-row'}
                    onToggle={(isOpen) =>
                      setOpenPopover(isOpen ? 'selected-row' : null)
                    }
                  >
                    {selectedModalAiCreditUsagePerRow * adjustedSelectionCount >
                    999 ? (
                      <OverlayTooltip
                        text={
                          selectedModalAiCreditUsagePerRow *
                          adjustedSelectionCount
                        }
                      >
                        <div
                          className="credits"
                          {...accessibleOnClick((e) => e.stopPropagation())}
                        >
                          <ImageIcon src={Images?.AiCredits} alt="credits" />
                          <span
                            className="credits-amount"
                            {...accessibleOnClick((e) => {
                              handleCreditsPopoverToggle(e, 'selected-row');
                            })}
                          >
                            ~
                            {`${formatNumber(
                              Number(
                                (
                                  selectedModalAiCreditUsagePerRow *
                                  adjustedSelectionCount
                                ).toFixed(2),
                              ),
                            )}`}
                          </span>
                        </div>
                      </OverlayTooltip>
                    ) : (
                      <div
                        className="credits"
                        {...accessibleOnClick((e) => e.stopPropagation())}
                      >
                        <ImageIcon src={Images?.AiCredits} alt="credits" />
                        <span
                          className="credits-amount"
                          {...accessibleOnClick((e) => {
                            handleCreditsPopoverToggle(e, 'selected-row');
                          })}
                        >
                          ~
                          {`${formatNumber(
                            Number(
                              (
                                selectedModalAiCreditUsagePerRow *
                                adjustedSelectionCount
                              ).toFixed(2),
                            ),
                          )}`}
                        </span>
                      </div>
                    )}
                  </OverlayPopover>
                </>
              ),
              ...accessibleOnClick(() => {
                handleSaveAction(AiColumnsActionType.SaveAndRunForSelectedRows);
              }),
            },
          ]
        : []),
      // Save and run for empty rows
      ...(!isNewColumnName
        ? [
            {
              listKey: 'empty-rows',
              listLabel: (
                <>
                  <div className="save-credits">
                    <span className="save-option">Update Empty Rows</span>
                  </div>
                </>
              ),
              ...accessibleOnClick(() => {
                handleSaveAction(AiColumnsActionType.SaveAndRunForEmptyRows);
              }),
            },
          ]
        : []),

      {
        listKey: 'dont-run',
        listLabel: (
          <>
            <div className="save-credits">
              <span className="save-option">Save & Don’t Run</span>
            </div>
          </>
        ),
        ...accessibleOnClick(() => {
          handleSaveAction(AiColumnsActionType.SaveAndDontRun);
        }),
      },
    ];

    const [isPreviewModalOpen, setIsPreviewModalOpen] = useState(false);
    const [isSaveAsTemplateModalOpen, setIsSaveAsTemplateModalOpen] = useState(
      false,
    );

    const openPreviewModal = () => {
      setIsPreviewModalOpen(true);
      setPreviewModalOpen(true);
    };
    const closePreviewModal = () => {
      setIsPreviewModalOpen(false);
      setPreviewModalOpen(false);
    };

    const openSaveAsTemplateModal = () => setIsSaveAsTemplateModalOpen(true);
    const closeSaveAsTemplateModal = () => setIsSaveAsTemplateModalOpen(false);

    // Prepare template data for the modal
    const enrichPromptDataForModal = {
      title: templateData?.name,
      description: templateData?.description,
      isWebSearchEnabled: enrichPromptData?.isWebSearchEnabled,
      mergeTags: enrichPromptData?.mergeTags || [],
      shSupportedAiModel: Number(enrichPromptData?.aiModelId) || 0,
      instructions: enrichPromptData?.instructions || '',
      selectedOutputFormat: enrichPromptData?.selectedOutputFormat || '',
      customOutputFormatPrompt:
        enrichPromptData?.customOutputFormatInput || undefined,
      customOutputFormatColumnType: enrichPromptData?.selectedType || undefined,
      isAutoRunEnabled: enrichPromptData?.isAutoRunEnabled || false,
      autoRunConditions: enrichPromptData?.autoRunConditions || null,
    };

    return (
      <div
        className={`template-footer-container ${
          disableFooter ? 'disabled' : ''
        }`}
        ref={cellRef}
      >
        <Button
          className="save-as-template"
          {...accessibleOnClick(openSaveAsTemplateModal)}
          disabled={footerDisabled}
        >
          <Plus className="template-plus-icon" />
          <span>Save as Template</span>
        </Button>
        <AiColSaveAsTemplateModal
          show={isSaveAsTemplateModalOpen}
          onClose={closeSaveAsTemplateModal}
          templateData={enrichPromptDataForModal}
          templateId={templateId}
          currentStep={currentStep}
        />
        <div className="template-preview-container">
          <Button
            className={`template-preview ${isPreviewModalOpen ? 'open' : ''}`}
            {...accessibleOnClick(() => {
              openPreviewModal();
              setIsLoadingState(true);
            })}
            disabled={footerDisabled || prospectList?.length === 0}
          >
            <Preview className="preview-icon" />
            <span>Preview</span>
          </Button>
          <AiColPromptPreviewModal
            show={isPreviewModalOpen}
            onClose={closePreviewModal}
            enrichPromptData={enrichPromptData}
            isLoadingState={isLoadingState}
            setIsLoadingState={setIsLoadingState}
            selectedModalAiCreditUsagePerRow={selectedModalAiCreditUsagePerRow}
          />
          <OverrideGeneratedExistingColumn
            show={overrideModalOpen}
            onClose={handleOverrideModalClose}
            onProceed={handleOverrideModalProceed}
          />
          <OverlayPopover
            show={savePopoverOpen}
            onToggle={(isOpen) => setSavePopoverOpen(isOpen)}
            className="save-dropdown"
            content={
              <div className="save-dropdown-content" ref={popoverRef}>
                {options.map((option) => (
                  <div
                    key={option.listKey}
                    className="save-dropdown-item"
                    {...option}
                  >
                    {option.listLabel}
                  </div>
                ))}
              </div>
            }
            placement={Placement.Top}
          >
            <Button
              className="save-dropdown-btn"
              disabled={
                footerDisabled ||
                prospectList?.length === 0 ||
                isSaveDisabledForColumnName
              }
              {...accessibleOnClick(() => handleRedirectToListView())}
            >
              Save
              <div className="pl-1">
                <ChevronDown />
              </div>
            </Button>
          </OverlayPopover>
        </div>
      </div>
    );
  },
);

export default TemplateFooter;
